// Shared REST/relay attribution for board cards and task details. No polling or DOM dependencies.
const LIVE = new Set(['starting', 'running', 'disconnected']);
const TEXT = {
  en: { device: 'Device', starting: 'Starting', running: 'Running', disconnected: 'Disconnected', recent: 'Last worked',
    startingLabel: 'Starting on {device}', runningLabel: 'Running on {device}', disconnectedLabel: 'Disconnected from {device}', recentLabel: 'Last worked on {device}' },
  uk: { device: 'Пристрій', starting: 'Запускається', running: 'Виконується', disconnected: 'Відключено', recent: 'Остання робота',
    startingLabel: 'Запускається на {device}', runningLabel: 'Виконується на {device}', disconnectedLabel: 'Відключено від {device}', recentLabel: 'Остання робота на {device}' },
};
const copy = language => TEXT[language] ?? TEXT.en;
const escape = value => String(value ?? '').replace(/[&<>"']/g, char => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[char]));
const time = value => Date.parse(value) || 0;
const newer = (a, b) => time(a.started_at) - time(b.started_at) || String(a.session_id).localeCompare(String(b.session_id));

export const deviceFieldLabel = language => copy(language).device;

export function renderDeviceBadge(task, language = 'en') {
  const device = task.active_device ?? task.recent_device;
  if (!device) return '';
  // Older APIs know only the claim: without presence it cannot be called running.
  const state = task.active_device ? (LIVE.has(device.state) ? device.state : 'disconnected') : 'recent';
  const text = copy(language);
  const label = text[`${state}Label`].replace('{device}', device.name);
  return `<span class="device-badge device-badge--${state}" title="${escape(label)}" aria-label="${escape(label)}">${escape(device.name)} · ${text[state]}</span>`;
}

// Stable empty slots let lifecycle updates leave focused cards, drags and editors in place.
export function renderDeviceAttribution(task, language) {
  return `<span data-device-attribution="${escape(task.task_key)}">${renderDeviceBadge(task, language)}</span>`;
}

export function syncDeviceBadges(root, tasks, language) {
  const byKey = new Map(tasks.map(task => [task.task_key, task]));
  for (const slot of root?.querySelectorAll('[data-device-attribution]') ?? []) {
    const task = byKey.get(slot.dataset.deviceAttribution);
    if (!task) continue;
    const html = renderDeviceBadge(task, language);
    if (slot.innerHTML !== html) slot.innerHTML = html;
    const field = slot.closest('[data-device-field]');
    if (field) field.hidden = !html;
  }
}

export function createDeviceSessionScope(projectId) {
  return { projectId: Number(projectId), sessions: new Map(), ended: new Set(), subscribed: false, connection: 'open' };
}

// Reapply after REST reloads too: HTTP replies can predate the socket event that arrived first.
export function applyDeviceSessionEvent(tasks, event, scope) {
  if (event.projectId != null && Number(event.projectId) !== scope.projectId) return false;
  if (event.type === 'connection') {
    scope.connection = event.connection;
    if (event.connection !== 'open') scope.subscribed = false;
  }
  else if (event.type === 'subscribed') {
    scope.sessions = new Map((event.sessions ?? [])
      .filter(session => Number(session.project_id) === scope.projectId && LIVE.has(session.state))
      .map(session => [session.session_id, session]));
    scope.subscribed = true;
    scope.connection = 'open';
  } else if (event.type === 'session') {
    const session = event.session;
    if (Number(session?.project_id) !== scope.projectId) return false;
    const previous = scope.sessions.get(session.session_id);
    if (scope.ended.has(session.session_id) || (previous && time(previous.updated_at) > time(session.updated_at))) return false;
    if (LIVE.has(session.state)) scope.sessions.set(session.session_id, session);
    else {
      scope.sessions.delete(session.session_id);
      scope.ended.add(session.session_id);
    }
  } else if (event.type === 'revoked') {
    scope.sessions.clear();
    scope.subscribed = true;
  } else if (event.type !== 'reconcile') return false;

  let changed = false;
  const newestByTask = new Map();
  for (const session of scope.sessions.values()) {
    const previous = newestByTask.get(session.task_key);
    if (!previous || newer(session, previous) > 0) newestByTask.set(session.task_key, session);
  }
  for (const task of tasks) {
    const before = JSON.stringify([task.active_device, task.recent_device]);
    const current = task.active_device;
    const session = newestByTask.get(task.task_key);
    if (session && (!current?.session_id || current.session_id === session.session_id || newer(session, current) >= 0)) {
      task.active_device = { id: session.device_id, name: session.device_name, session_id: session.session_id,
        started_at: session.started_at, state: scope.connection === 'open' && scope.subscribed ? session.state : 'disconnected' };
    } else if (current) {
      if (event.type === 'revoked' || (current.session_id && (scope.ended.has(current.session_id) || event.type === 'subscribed'))) {
        task.recent_device = { id: current.id, name: current.name };
        task.active_device = null;
      } else if (scope.connection !== 'open' || (scope.subscribed && !current.session_id)) {
        task.active_device = { ...current, state: 'disconnected' };
      }
    }
    if (JSON.stringify([task.active_device, task.recent_device]) !== before) changed = true;
  }
  return changed;
}
